// Environment variable validation and security utilities

const ENV_CONFIG = {
  // Required environment variables
  required: {
    VITE_SUPABASE_URL: {
      description: 'Supabase project URL',
      validate: (value) => value && value.startsWith('https://') && value.includes('supabase.co'),
      error: 'VITE_SUPABASE_URL must be a valid Supabase URL'
    },
    VITE_SUPABASE_ANON_KEY: {
      description: 'Supabase anonymous key',
      validate: (value) => value && value.startsWith('eyJ') && value.length > 100,
      error: 'VITE_SUPABASE_ANON_KEY must be a valid JWT token'
    }
  },
  
  // Optional environment variables with defaults
  optional: {
    VITE_GOOGLE_ANALYTICS_ID: {
      description: 'Google Analytics Measurement ID',
      default: null,
      validate: (value) => !value || value.startsWith('G-')
    },
    VITE_GA_MEASUREMENT_ID: {
      description: 'Legacy Google Analytics ID',
      default: null,
      validate: (value) => !value || value.startsWith('G-')
    },
    VITE_CALENDLY_BASE_URL: {
      description: 'Calendly base URL',
      default: null,
      validate: (value) => !value || value.startsWith('https://calendly.com/')
    },
    VITE_CALENDLY_30MIN_URL: {
      description: 'Calendly 30min booking URL',
      default: null,
      validate: (value) => !value || value.startsWith('https://calendly.com/')
    },
    VITE_ENCRYPTION_SECRET: {
      description: 'Encryption secret for localStorage',
      default: 'comeyvivesano-default-key-2024',
      validate: (value) => value && value.length >= 16
    }
  },
  
  // Sensitive keys that should NOT be set to default values in production
  sensitive: {
    VITE_OPENAI_API_KEY: {
      description: 'OpenAI API Key',
      placeholder: 'your-openai-api-key-here'
    },
    VITE_GEMINI_API_KEY: {
      description: 'Google Gemini API Key',
      placeholder: 'your-gemini-api-key-here'
    },
    VITE_ANTHROPIC_API_KEY: {
      description: 'Anthropic API Key',
      placeholder: 'your-anthropic-api-key-here'
    },
    VITE_PERPLEXITY_API_KEY: {
      description: 'Perplexity API Key',
      placeholder: 'your-perplexity-api-key-here'
    },
    VITE_STRIPE_PUBLISHABLE_KEY: {
      description: 'Stripe Publishable Key',
      placeholder: 'your-stripe-publishable-key-here'
    },
    VITE_CALENDLY_ACCESS_TOKEN: {
      description: 'Calendly Access Token',
      placeholder: 'your_calendly_access_token_here'
    }
  }
};

// Validate environment configuration
export const validateEnvironment = () => {
  const errors = [];
  const warnings = [];
  const isProduction = import.meta.env.PROD;

  // Check required variables
  Object.entries(ENV_CONFIG.required).forEach(([key, config]) => {
    const value = import.meta.env[key];
    
    if (!value) {
      errors.push(`Missing required environment variable: ${key} - ${config.description}`);
    } else if (config.validate && !config.validate(value)) {
      errors.push(`Invalid ${key}: ${config.error || 'Invalid format'}`);
    }
  });

  // Check optional variables
  Object.entries(ENV_CONFIG.optional).forEach(([key, config]) => {
    const value = import.meta.env[key];
    
    if (value && config.validate && !config.validate(value)) {
      warnings.push(`Invalid optional ${key}: Expected ${config.description}`);
    }
  });

  // Check sensitive variables in production
  if (isProduction) {
    Object.entries(ENV_CONFIG.sensitive).forEach(([key, config]) => {
      const value = import.meta.env[key];
      
      if (value === config.placeholder) {
        warnings.push(`${key} is still set to placeholder value in production`);
      }
    });
  }

  return {
    isValid: errors.length === 0,
    errors,
    warnings,
    environment: {
      isDevelopment: import.meta.env.DEV,
      isProduction: import.meta.env.PROD,
      mode: import.meta.env.MODE
    }
  };
};

// Get environment variable with validation and fallback
export const getEnvVar = (key, fallback = null) => {
  const value = import.meta.env[key];
  
  // Check if it's a required variable
  if (ENV_CONFIG.required[key]) {
    if (!value) {
      throw new Error(`Missing required environment variable: ${key}`);
    }
    if (ENV_CONFIG.required[key].validate && !ENV_CONFIG.required[key].validate(value)) {
      throw new Error(`Invalid ${key}: ${ENV_CONFIG.required[key].error}`);
    }
    return value;
  }
  
  // Check if it's an optional variable
  if (ENV_CONFIG.optional[key]) {
    const effectiveValue = value || ENV_CONFIG.optional[key].default || fallback;
    if (effectiveValue && ENV_CONFIG.optional[key].validate && !ENV_CONFIG.optional[key].validate(effectiveValue)) {
      console.warn(`Invalid optional ${key}, using fallback`);
      return fallback;
    }
    return effectiveValue;
  }
  
  // Check if it's a sensitive variable
  if (ENV_CONFIG.sensitive[key]) {
    if (value === ENV_CONFIG.sensitive[key].placeholder) {
      console.warn(`${key} is set to placeholder value - functionality may be limited`);
      return null; // Return null for placeholder values
    }
    return value || fallback;
  }
  
  // Unknown variable
  return value || fallback;
};

// Security utilities for API keys
export const isApiKeyValid = (apiKey, service) => {
  if (!apiKey || typeof apiKey !== 'string') return false;
  
  const patterns = {
    openai: /^sk-[A-Za-z0-9]{48}$/,
    stripe: /^pk_test_|^pk_live_/,
    supabase: /^eyJ[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*\.[A-Za-z0-9_-]*$/
  };
  
  if (patterns[service]) {
    return patterns[service].test(apiKey);
  }
  
  // Generic validation for unknown services
  return apiKey.length > 10 && !apiKey.includes('your-') && !apiKey.includes('here');
};

// Mask sensitive values for logging
export const maskSensitiveValue = (value, keepChars = 4) => {
  if (!value || typeof value !== 'string') return '[MISSING]';
  if (value.length <= keepChars * 2) return '[HIDDEN]';
  
  return value.substring(0, keepChars) + 
         '*'.repeat(Math.max(value.length - keepChars * 2, 3)) + 
         value.substring(value.length - keepChars);
};

export default {
  validateEnvironment,
  getEnvVar,
  isApiKeyValid,
  maskSensitiveValue,
  ENV_CONFIG
};